'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const isDebugErrorsEnabled = () => {
  if (typeof window === 'undefined') return false;

  try {
    const url = new URL(window.location.href);
    const queryDebug = url.searchParams.get('debug-errors');

    if (queryDebug === '1' || queryDebug === 'true') return true;

    return window.localStorage.getItem('debug-errors') === '1';
  } catch {
    return false;
  }
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in production for debugging
    if (typeof window !== 'undefined') {
      // Log to console for debugging (will be visible in browser console)
      // In production, you can also send to error tracking service
      const errorDetails = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      this.setState({ errorInfo });

      // Log error details
      // You can also send to error tracking service like Sentry here
      const debugEnabled = isDebugErrorsEnabled();

      if (process.env.NODE_ENV === 'production' && !debugEnabled) {
        console.error('[ErrorBoundary]', {
          message: error.message,
          url: window.location.href,
          timestamp: errorDetails.timestamp,
        });

        return;
      }

      console.error('[ErrorBoundary]', errorDetails);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
            <h1 className="mb-4 text-2xl font-bold text-gray-900">Something went wrong</h1>
            <p className="mb-4 text-gray-600">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            {(process.env.NODE_ENV === 'development' || isDebugErrorsEnabled()) && this.state.error && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-gray-500">Error details</summary>
                <pre className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs">
                  {this.state.error.toString()}
                  {this.state.error.stack}
                  {this.state.errorInfo?.componentStack}
                  {(() => {
                    const detail = (this.state.error as any)?.detail;

                    if (!detail) return null;

                    try {
                      return `\n\nDetail:\n${JSON.stringify(detail, null, 2)}`;
                    } catch {
                      return '\n\nDetail: [unserializable]';
                    }
                  })()}
                </pre>
              </details>
            )}
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                window.location.reload();
              }}
              className="w-full rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
